import { DatePicker } from '../../../components/ui/date-picker';
// apps/web/src/pages/vendas/precos/aba-tinta.tsx — Fase 12, Plano 06 (PV-09, D-13, D-14).
// Aba "Tinta" da Tabela de Preços: preço de cada tinta (com fornecedor) versionado por vigência.
import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, X } from 'lucide-react';
import type { MaterialLookupPv, PrecoTintaVersaoView } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { formatarData, formatarMoeda } from '../../../lib/vendas/formatacao';
import { BadgeEstadoVersao } from './badge-estado-versao';
import { MaterialCombobox } from '../../../components/vendas/material-combobox';
import { NovaVersaoSheet } from './nova-versao-sheet';

/** Histórico: agrupa por tinta + fornecedor e ordena da versão mais recente para a mais antiga. */
function ordenarTintas(linhas: readonly PrecoTintaVersaoView[]): PrecoTintaVersaoView[] {
  return [...linhas].sort(
    (a, b) =>
      a.tintaCodigo.localeCompare(b.tintaCodigo, 'pt-BR') ||
      (a.fornecedorNome ?? '').localeCompare(b.fornecedorNome ?? '', 'pt-BR') ||
      b.vigenciaInicio.localeCompare(a.vigenciaInicio),
  );
}

export function AbaTinta() {
  const [linhas, setLinhas] = useState<readonly PrecoTintaVersaoView[] | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [tinta, setTinta] = useState<MaterialLookupPv | null>(null);
  const [fornecedor, setFornecedor] = useState('');
  const [fornecedorAplicado, setFornecedorAplicado] = useState('');
  const [vigentesEm, setVigentesEm] = useState('');
  const [historico, setHistorico] = useState(false);
  const [sheetAberto, setSheetAberto] = useState(false);
  const [base, setBase] = useState<PrecoTintaVersaoView | null>(null);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      setCarregando(true);
      setErro(false);
      try {
        const params = new URLSearchParams();
        if (tinta) params.set('tintaMaterialId', tinta.id);
        if (fornecedorAplicado) params.set('fornecedor', fornecedorAplicado);
        if (vigentesEm) params.set('vigentesEm', vigentesEm);
        if (historico) params.set('historico', 'true');
        const consulta = params.toString();
        const res = await fetch(`/api/v1/vendas/precos/tintas${consulta ? `?${consulta}` : ''}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as PrecoTintaVersaoView[];
        if (!sinal || sinal.vivo) setLinhas(ordenarTintas(json));
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregando(false);
      }
    },
    [tinta, fornecedorAplicado, vigentesEm, historico],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  function abrirSheet(linha: PrecoTintaVersaoView | null): void {
    setBase(linha);
    setSheetAberto(true);
  }

  const comFiltro = tinta !== null || fornecedorAplicado !== '' || vigentesEm !== '' || historico;
  const vazio = !carregando && !erro && (linhas?.length ?? 0) === 0 && !comFiltro;
  const semResultado = !carregando && !erro && (linhas?.length ?? 0) === 0 && comFiltro;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3" role="group" aria-label="Filtros dos preços de tinta">
        <div className="flex min-w-[16rem] flex-col gap-1">
          <label htmlFor="filtro-preco-tinta" className="text-xs font-medium text-slate-600">
            Item
          </label>
          <div className="flex items-center gap-1">
            <div className="flex-1">
              <MaterialCombobox id="filtro-preco-tinta" value={tinta} onChange={setTinta} />
            </div>
            {tinta && (
              <Button type="button" variant="ghost" size="icon" aria-label="Limpar tinta" onClick={() => setTinta(null)}>
                <X aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>
        <form
          className="flex flex-col gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            setFornecedorAplicado(fornecedor.trim());
          }}
        >
          <label htmlFor="filtro-preco-fornecedor" className="text-xs font-medium text-slate-600">
            Fornecedor da tinta
          </label>
          <Input
            id="filtro-preco-fornecedor"
            value={fornecedor}
            maxLength={120}
            placeholder="Nome do fornecedor"
            onChange={(e) => setFornecedor(e.target.value)}
            onBlur={() => setFornecedorAplicado(fornecedor.trim())}
          />
        </form>
        <div className="flex flex-col gap-1">
          <label htmlFor="filtro-tinta-vigentes-em" className="text-xs font-medium text-slate-600">
            Vigentes em
          </label>
          <DatePicker id="filtro-tinta-vigentes-em" value={vigentesEm} onValueChange={(value) => setVigentesEm(value)} />
        </div>
        <label htmlFor="filtro-tinta-historico" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 text-sm">
          <Checkbox id="filtro-tinta-historico" checked={historico} onCheckedChange={(marcada) => setHistorico(marcada === true)} />
          Mostrar histórico
        </label>
        <div className="ml-auto">
          <Button type="button" onClick={() => abrirSheet(null)}>
            <Plus aria-hidden="true" />
            Novo preço
          </Button>
        </div>
      </div>

      {erro ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar a tabela de preços. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : carregando && !linhas ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando preços de tinta">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : vazio ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum preço cadastrado</h2>
            <p className="max-w-xl text-sm text-muted-foreground">Cadastre o preço de cada tinta, com o fornecedor, para apurar o custo de tinta dos serviços.</p>
            <Button type="button" onClick={() => abrirSheet(null)}>
              <Plus aria-hidden="true" />
              Novo preço
            </Button>
          </CardContent>
        </Card>
      ) : semResultado ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum preço encontrado</h2>
            <p className="text-sm text-muted-foreground">Ajuste os filtros ou marque &quot;Mostrar histórico&quot;.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden py-0 gap-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Tinta</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Fornecedor</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Unidade de consumo</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-right">Preço</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Vigente a partir de</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Vigente até</th>
                <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Estado</th>
                <th scope="col" className="px-3 py-2">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {linhas?.map((linha) => (
                <tr key={linha.id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2 max-w-[20rem] truncate">
                    <span className="font-medium tabular-nums">{linha.tintaCodigo}</span> — {linha.tintaNome}
                  </td>
                  <td className="px-3 py-2 max-w-[14rem] truncate">{linha.fornecedorNome ?? '—'}</td>
                  <td className="px-3 py-2">{linha.unidadeConsumo}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatarMoeda(linha.preco, 4)}</td>
                  <td className="px-3 py-2 tabular-nums">{formatarData(linha.vigenciaInicio)}</td>
                  <td className="px-3 py-2 tabular-nums">{formatarData(linha.vigenciaFim)}</td>
                  <td className="px-3 py-2">
                    <BadgeEstadoVersao estado={linha.estado} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button type="button" variant="outline" size="sm" onClick={() => abrirSheet(linha)} aria-label={`Nova versão de ${linha.tintaCodigo}`}>
                      Nova versão
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <NovaVersaoSheet aberto={sheetAberto} onAbertoChange={setSheetAberto} alvo="TINTA" base={null} baseTinta={base} onSalvo={() => void carregar()} />
    </div>
  );
}
